import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  calculateCommittedPercentage,
  calculateRemainingDays,
  displayOpportunityAmount,
  formatTitleCaseWords,
  trimText,
} from '../../lib/utils/helpers'
import type { RawOpportunity } from '../../lib/types'
import type { BadgeV2Type } from './investment-card-v2'

dayjs.extend(utc)

export function formatTimeRemaining(closingDate: string): string {
  const end = dayjs.utc(closingDate).endOf('day')
  const now = dayjs.utc()
  if (end.isBefore(now)) return 'Closed'
  const days = calculateRemainingDays(now.toDate(), end.toDate())
  if (days === 0) {
    const hours = end.diff(now, 'hour')
    return hours <= 0 ? 'Closing soon' : `${hours} Hours`
  }
  if (days === 1) return '1 day'
  return `${days}d`
}

/** Returns empty array; badges are not shown on v2 opportunity cards. */
export function getBadgesForOpportunity(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- signature kept for API; badges not shown
  _opp: RawOpportunity,
): { type: BadgeV2Type; label: string }[] {
  return []
}

export function opportunityToCardProps(
  opp: RawOpportunity,
  onCtaClick: () => void,
  options?: { alwaysLearnMoreCta?: boolean; enableHighlight?: boolean },
): {
  id: number
  projectSlug: string
  title: string
  category: string
  description: string
  imageUrl: string
  fundingProgressPercent: number
  targetAmount: string
  minInvestment: string
  timeRemaining: string
  assetClass: string
  badges: { type: BadgeV2Type; label: string }[]
  ctaLabel: string
  onCtaClick: () => void
  dataTestId: string
  isHighlighted: boolean
} {
  const percentStr = calculateCommittedPercentage(
    opp.total_investment_amount || '0',
    opp.raise_goal,
    opp.asset_class,
    opp.asset_class_data,
  )
  const fundingProgressPercent = parseFloat(percentStr.replace('%', '')) || 0
  const targetAmount = displayOpportunityAmount(
    opp.raise_goal,
    opp.currency_code,
    opp.asset_class,
    opp.asset_class_data,
  )
  const minInvestment = displayOpportunityAmount(
    opp.ticket_size,
    opp.currency_code,
    opp.asset_class,
    opp.asset_class_data,
  )
  const alwaysLearnMore = options?.alwaysLearnMoreCta === true
  return {
    id: opp.id,
    projectSlug: opp.opportunity_slug,
    title: opp.opportunity_title,
    category: formatTitleCaseWords(opp.asset_class ?? ''),
    description: trimText(opp.short_opportunity_summary ?? opp.opportunity_summary, 120),
    imageUrl: opp.cover_image ?? opp.images?.[0] ?? '',
    fundingProgressPercent,
    targetAmount,
    minInvestment,
    timeRemaining: formatTimeRemaining(opp.closing_date),
    assetClass: opp.asset_class ?? '',
    badges: getBadgesForOpportunity(opp),
    ctaLabel: alwaysLearnMore
      ? 'Learn more'
      : fundingProgressPercent >= 100
      ? 'View detail'
      : 'Learn more',
    onCtaClick,
    dataTestId: `home-v2-opportunity-card-${opp.id}`,
    isHighlighted: options?.enableHighlight === true && opp.metadata?.highlight_enabled === true,
  }
}
